import React from "react";
import {
  Drawer,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Label from "../../../../components/Label/Label";
import function_icon from "../../../../assets/function_icon.png";
import VerifiedTwoToneIcon from "@mui/icons-material/VerifiedTwoTone";
import { getApplicationStatusStyle } from "../../../../constants/statusStyles";
import { Close } from "@mui/icons-material";
import PartialBatchMissingValueNote from "../../../../components/PartialBatchMissingValueNote/PartialBatchMissingValueNote";
import { isBlankBatchValue } from "../../../../utils/batchesUtility";
import { PARTIAL_BATCH_MISSING_VALUE_COLORS } from "../../../../constants/analyticsBatches";

const BatchDetailsDrawer = ({ open, onClose, status, isPartialBatch = false }) => {
  const { loader, data, apiError, message } = useSelector(
    (state) => state.batch?.batchMetaData || {}
  );

  const groupByPhase = (arr, key) => {
    return arr.reduce((acc, item) => {
      const phase = item[key] || "";
      if (!acc[phase]) acc[phase] = [];
      acc[phase].push(item);
      return acc;
    }, {});
  };

  const grouped = groupByPhase(data?.data || [], "phase");

  // Extract items with no phase
  const noPhaseItems = grouped[""] || [];
  delete grouped[""];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "700px" } }}
    >
      <Box p={2} pt={1}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Label
            text={`Batch Details - ${data?.batchId}`}
            fontSize="16px"
            fontWeight="500"
          />
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Loader */}
        {loader && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Error */}
        {apiError && <Label text={message} />}

        {/* No Data */}
        {!(
          (Array.isArray(data?.data) && data?.data.length > 0) ||
          loader
        ) && (
            <Label
              color="textSecondary"
              textAlign="center"
              text={"No data found"}
            />
          )}

        {/* Data */}
        {Array.isArray(data?.data) && data?.data.length > 0 && (
          <>
            {/* Status Banner */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor:
                  getApplicationStatusStyle(status)?.backgroundColor || "lightgrey",
                padding: "12px 16px",
                borderRadius: "8px",
                mb: 2,
              }}
            >
              <Box display="flex" alignItems="center">
                <VerifiedTwoToneIcon
                  sx={{
                    fontSize: "20px",
                    fill: getApplicationStatusStyle(status)?.color || "black",
                    marginRight: "8px",
                  }}
                />
                <Label
                  text={`Batch Status: ${status || ""}`}
                  fontSize="14px"
                  fontWeight="700"
                  color={getApplicationStatusStyle(status)?.color || "black"}
                />
              </Box>
            </Box>

            {isPartialBatch && (
              <Box mb={2}>
                <PartialBatchMissingValueNote />
              </Box>
            )}

            {/* NO PHASE ITEMS (TOP) */}
            {noPhaseItems.length > 0 && (
              <Box mb={3}>


                <Grid container spacing={2}>
                  {noPhaseItems.map((item, idx) => (
                    <Grid item xs={6} key={`no-phase-${idx}`}>
                      <Card
                        sx={{
                          p: "8px 16px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          borderRadius: "8px",
                          border:
                            isPartialBatch && isBlankBatchValue(item.value_text)
                              ? `1px solid ${PARTIAL_BATCH_MISSING_VALUE_COLORS.border}`
                              : "1px solid #ccc",
                          backgroundColor:
                            isPartialBatch && isBlankBatchValue(item.value_text)
                              ? PARTIAL_BATCH_MISSING_VALUE_COLORS.background
                              : "#fff",
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 0,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            justifyContent: "space-between",
                            "&:last-child": { pb: 0 },
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <VisibilityIcon sx={{ fontSize: "14px" }} />
                              <Label textTransform={'none'} text={item.label} />
                            </Box>

                            {item.source_system === "Derived" && (
                              <img
                                src={function_icon}
                                alt="function"
                                width={16}
                                height={16}
                              />
                            )}
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <VerifiedTwoToneIcon
                                sx={{
                                  fontSize: "14px",
                                  fill:
                                    isPartialBatch &&
                                    isBlankBatchValue(item.value_text)
                                      ? PARTIAL_BATCH_MISSING_VALUE_COLORS.border
                                      : "green",
                                }}
                              />
                              <Label
                                textTransform={'none'}
                                text={
                                  isPartialBatch &&
                                  isBlankBatchValue(item.value_text)
                                    ? ""
                                    : item.value_text
                                }
                                fontWeight="600"
                                color={
                                  isPartialBatch &&
                                  isBlankBatchValue(item.value_text)
                                    ? PARTIAL_BATCH_MISSING_VALUE_COLORS.text
                                    : undefined
                                }
                              />
                            </Box>

                            {item.unit_of_measure && (
                              <Label text={item.unit_of_measure} textTransform={'none'} />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* PHASED ITEMS */}
            {Object.entries(grouped).map(([phase, items]) => (
              <Box key={phase} mb={3}>
                {phase && (
                  <Typography
                    variant="h6"
                    sx={{
                      p: 0.5,
                      fontSize: 14,
                      fontWeight: 600,
                      mb: 1,
                      textTransform: "capitalize",
                      backgroundColor: "#e6ebf2",
                    }}
                  >
                    {phase}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  {items.map((item, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Card
                        sx={{
                          p: "8px 16px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          borderRadius: "8px",
                          border:
                            isPartialBatch && isBlankBatchValue(item.value_text)
                              ? `1px solid ${PARTIAL_BATCH_MISSING_VALUE_COLORS.border}`
                              : "1px solid #ccc",
                          backgroundColor:
                            isPartialBatch && isBlankBatchValue(item.value_text)
                              ? PARTIAL_BATCH_MISSING_VALUE_COLORS.background
                              : "#fff",
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 0,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            justifyContent: "space-between",
                            "&:last-child": { pb: 0 },
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <VisibilityIcon sx={{ fontSize: "14px" }} />
                              <Label text={item.label} textTransform={'none'}/>
                            </Box>

                            {item.source_system === "Derived" && (
                              <img
                                src={function_icon}
                                alt="function"
                                width={16}
                                height={16}
                              />
                            )}
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <VerifiedTwoToneIcon
                                sx={{
                                  fontSize: "14px",
                                  fill:
                                    isPartialBatch &&
                                    isBlankBatchValue(item.value_text)
                                      ? PARTIAL_BATCH_MISSING_VALUE_COLORS.border
                                      : "green",
                                }}
                              />
                              <Label
                                text={
                                  isPartialBatch &&
                                  isBlankBatchValue(item.value_text)
                                    ? ""
                                    : item.value_text
                                }
                                fontWeight="600"
                                textTransform={'none'}
                                color={
                                  isPartialBatch &&
                                  isBlankBatchValue(item.value_text)
                                    ? PARTIAL_BATCH_MISSING_VALUE_COLORS.text
                                    : undefined
                                }
                              />
                            </Box>

                            {item.unit_of_measure && (
                              <Label text={item.unit_of_measure} textTransform={'none'} />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default BatchDetailsDrawer;
